import React, { Fragment, useEffect, useState } from "react";

import sentry from "@common/assets/images/integrations/sentry-icon.svg";
import { DropdownItem } from "@common/components/Dropdown/Dropdown";
import Icon from "@common/components/Icon/Icon";
import { SentryNudge } from "@common/components/Nudge/Nudge";
import { Workspace } from "@common/models";
import { Sentry, SentryIntegrationInfo, SentryIntegrationIssue } from "@common/models/integrations";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";

import * as Styled from "./SentryIntegrationContainer.styles";
import { SentryIssueRow } from "./SentryIssueRow";

interface Props {
  sentryIntegration?: Sentry;
  fetchSentryProjects: () => Promise<SentryIntegrationInfo[] | undefined>;
  fetchSentryIssues: (projectSlug: string, environment: string) => Promise<SentryIntegrationIssue[] | undefined>;
  selectedWorkspace?: Workspace;
}

export const SentryIntegrationContainer = ({
  sentryIntegration,
  fetchSentryProjects,
  fetchSentryIssues,
  selectedWorkspace,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(sentryIntegration));
  const [sentryProjects, setSentryProjects] = useState<SentryIntegrationInfo[] | null>(null);
  const [sentryIssues, setSentryIssues] = useState<SentryIntegrationIssue[]>([]);
  const [selectedSentryProject, setSelectedSentryProject] = useState<SentryIntegrationInfo | null>(null);
  const [selectedSentryEnvironment, setSelectedSentryEnvironment] = useState<string>();
  const [isSentryProjectDropdownOpen, setIsSentryProjectDropdownOpen] = useState<boolean>(false);
  const [isSentryEnvironmentDropdownOpen, setIsSentryEnvironmentDropdownOpen] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || sentryProjects !== null) return;

    const getSentryProjects = async () => {
      try {
        setLoading(true);

        const projects: SentryIntegrationInfo[] = (await fetchSentryProjects()) ?? [];
        setSentryProjects(projects);
        restoreSelectedSentryProject(projects);
      } catch (e) {
        console.error("Failed to fetch Sentry projects", e);
      } finally {
        setLoading(false);
      }
    };

    const restoreSelectedSentryProject = (projects: SentryIntegrationInfo[]) => {
      if (!selectedWorkspace?.id) return;

      const storedSentrySelection: SentrySelection | null = getSentrySelection(selectedWorkspace.id);
      if (storedSentrySelection) {
        const selectedSentryProject: SentryIntegrationInfo | null =
          projects?.find(
            (project) =>
              project.id === storedSentrySelection.selectedSentryId &&
              project.environments.includes(storedSentrySelection.selectedEnvironment),
          ) ?? null;
        if (selectedSentryProject) {
          setSelectedSentryProject(selectedSentryProject);
          setSelectedSentryEnvironment(storedSentrySelection.selectedEnvironment);
        } else {
          clearSentrySelection(selectedWorkspace?.id ?? "");
        }
      }
    };

    getSentryProjects();
  }, [isOpen, fetchSentryProjects, sentryProjects, selectedWorkspace?.id]);

  const selectSentryProject = async (sentryProject: SentryIntegrationInfo) => {
    setSelectedSentryProject(sentryProject);
    setIsSentryProjectDropdownOpen(false);

    await selectEnvironment(sentryProject, sentryProject.environments[0]);
  };

  const selectEnvironment = async (project: SentryIntegrationInfo, environment: string) => {
    setSelectedSentryEnvironment(environment);
    setIsSentryEnvironmentDropdownOpen(false);

    try {
      setLoading(true);

      const issues: SentryIntegrationIssue[] = (await fetchSentryIssues(project.slug, environment)) ?? [];
      setSentryIssues(issues);
      if (selectedWorkspace?.id) setSentrySelection(selectedWorkspace.id, project, environment);
    } catch (e) {
      console.error("Failed to fetch Sentry issues", e);
    } finally {
      setLoading(false);
    }
  };

  if (!sentryIntegration) return <SentryNudge />;

  return (
    <Styled.Table $enabled={true}>
      <Styled.TableHeader
        $isBodyOpen={isOpen}
        $enabled={true}
        onClick={() => !isOpen && setIsOpen(true)}
      >
        <Flex
          $gap={1}
          $alignItems="flex-start"
        >
          <Styled.Image src={sentry} />
          <Styled.Header>Sentry errors</Styled.Header>
          {isOpen && (
            <>
              <Styled.ProjectDropdown
                position="left"
                wide
                label={selectedSentryProject ? selectedSentryProject.name : "Select project"}
                isOpened={isSentryProjectDropdownOpen}
                setIsOpened={setIsSentryProjectDropdownOpen}
                buttonElement={
                  <Fragment>
                    <Icon
                      icon="caret-down"
                      size={10}
                    />
                  </Fragment>
                }
              >
                {sentryProjects &&
                  sentryProjects.map((sentry) => (
                    <DropdownItem
                      key={sentry.id}
                      onClick={() => selectSentryProject(sentry)}
                    >
                      {sentry.name}
                    </DropdownItem>
                  ))}
              </Styled.ProjectDropdown>
              {selectedSentryEnvironment && (
                <Styled.ProjectDropdown
                  position="left"
                  wide
                  label={selectedSentryEnvironment ? selectedSentryEnvironment : "Select environment"}
                  isOpened={isSentryEnvironmentDropdownOpen}
                  setIsOpened={setIsSentryEnvironmentDropdownOpen}
                  buttonElement={
                    <Fragment>
                      <Icon
                        icon="caret-down"
                        size={10}
                      />
                    </Fragment>
                  }
                >
                  {selectedSentryProject?.environments.map((environment) => (
                    <DropdownItem
                      key={environment}
                      onClick={() => selectEnvironment(selectedSentryProject, environment)}
                    >
                      {environment}
                    </DropdownItem>
                  ))}
                </Styled.ProjectDropdown>
              )}
            </>
          )}
        </Flex>
        <div
          style={{
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon
            icon={isOpen ? "caret-up" : "caret-down"}
            size={10}
          />
        </div>
      </Styled.TableHeader>
      {isOpen && (
        <Styled.TableBody>
          {loading || !selectedSentryProject ? (
            <Styled.GreyBox />
          ) : sentryIssues.length !== 0 ? (
            sentryIssues.map((issue) => (
              <SentryIssueRow
                issue={issue}
                key={issue.id}
              />
            ))
          ) : (
            <Styled.Description style={{ margin: "1.2rem 2rem" }}>No issues found</Styled.Description>
          )}
        </Styled.TableBody>
      )}
    </Styled.Table>
  );
};

interface SentrySelection {
  selectedSentryId: string;
  selectedEnvironment: string;
}

const setSentrySelection = (workspaceId: string, project: SentryIntegrationInfo, environment: string) => {
  const sentrySelection: SentrySelection = { selectedSentryId: project.id, selectedEnvironment: environment };
  localStorage.setItem(`sentry_${workspaceId}`, JSON.stringify(sentrySelection));
};

const getSentrySelection = (workspaceId: string): SentrySelection | null => {
  const storedData: string | null = localStorage.getItem(`sentry_${workspaceId}`);
  if (!storedData) return null;

  const parsedData: any = JSON.parse(storedData);
  if (!parsedData?.selectedSentryId || !parsedData?.selectedEnvironment) return null;

  return { selectedSentryId: parsedData.selectedSentryId, selectedEnvironment: parsedData.selectedEnvironment };
};

const clearSentrySelection = (workspaceId: string) => {
  localStorage.removeItem(`sentry_${workspaceId}`);
};
